import { TrendingDown, Users, CheckCircle, Timer } from "lucide-react";

const CLINICAL_BG = "/images/cami_p6_img2.jpeg";

const stats = [
  {
    value: "100%",
    label: "Patient Improvement",
    desc: "Every participant showed measurable results",
    icon: CheckCircle,
  },
  {
    value: "65%",
    label: "Significant Improvement",
    desc: "GAIS levels 3–5 achieved",
    icon: TrendingDown,
  },
  {
    value: "5.04 → 3.5",
    label: "Wrinkle Scale Reduction",
    desc: "Fitzpatrick score (P<0.001)",
    icon: TrendingDown,
  },
  {
    value: "4/10",
    label: "Pain Level",
    desc: "Mild to moderate with topical anesthesia",
    icon: Timer,
  },
];

const studyDetails = [
  { label: "Patients", value: "49" },
  { label: "Treatment Sessions", value: "3 monthly" },
  { label: "Follow-up", value: "3 months post" },
  { label: "Centers", value: "11 (US + Japan)" },
  { label: "Skin Types", value: "Fitzpatrick II–IV" },
  { label: "Adverse Events", value: "None significant" },
];

export default function ClinicalResults() {
  return (
    <section
      id="results"
      data-testid="clinical-results-section"
      className="py-24 lg:py-32 px-6 lg:px-12 relative overflow-hidden"
    >
      {/* Subtle background */}
      <div className="absolute inset-0 opacity-5">
        <img
          src={CLINICAL_BG}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="reveal mb-20">
          <p
            className="text-xs tracking-[0.3em] uppercase mb-4"
            style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
          >
            Multicenter Clinical Trial
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-white leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Proven Results.<br />
            <span className="text-white/40">IRB-Approved Evidence.</span>
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16 reveal">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="stat-card p-8"
                data-testid={`clinical-stat-${i}`}
              >
                <Icon size={18} className="mb-4" style={{ color: "#D4AF37" }} />
                <div
                  className="text-3xl lg:text-4xl font-light text-white mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-xs tracking-[0.1em] uppercase mb-2"
                  style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
                >
                  {stat.label}
                </div>
                <p className="text-xs text-white/30" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {stat.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Study Details */}
        <div className="reveal glass-card p-8 lg:p-12">
          <div className="flex items-center gap-3 mb-8">
            <Users size={16} style={{ color: "#D4AF37" }} />
            <p
              className="text-xs tracking-[0.2em] uppercase"
              style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
            >
              Study Parameters
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {studyDetails.map((detail) => (
              <div key={detail.label} className="flex justify-between items-baseline border-b border-white/5 pb-3">
                <span className="text-sm text-white/40" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {detail.label}
                </span>
                <span className="text-sm text-white font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {detail.value}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-white/[0.03] border border-white/5">
            <p className="text-sm text-white/50 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
              The multicenter prospective IRB-approved clinical trial demonstrated that 
              Cami X noninsulated fractional RF microneedling delivers significant wrinkle 
              reduction, skin tightening, and mid/lower face lifting. No unusual adverse events 
              including post-inflammatory hyperpigmentation, epidermal burns, or scar formation 
              were observed across both Caucasian and Asian skin types.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
